import React, { useEffect, useRef, useState } from 'react'
import styles from './TaskSortMenu.module.css'

export type TaskSortMode = 'alphabetical' | 'created' | 'priority'

/** Initial / recommended sort for the daily task list. */
export const DEFAULT_TASK_SORT: TaskSortMode = 'priority'

const OPTIONS: { value: TaskSortMode; label: string }[] = [
  { value: 'priority', label: 'Priority' },
  { value: 'alphabetical', label: 'A–Z' },
  { value: 'created', label: 'Date created' },
]

const currentLabel = (mode: TaskSortMode) =>
  OPTIONS.find((o) => o.value === mode)?.label ?? 'Sort'

const SortIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 8h4M4 12h8M4 16h12M15 5l3 3-3 3"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export type TaskSortMenuProps = {
  value: TaskSortMode
  onChange: (mode: TaskSortMode) => void
}

const TaskSortMenu = ({ value, onChange }: TaskSortMenuProps) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false)
    }
    window.addEventListener('mousedown', onDoc)
    return () => window.removeEventListener('mousedown', onDoc)
  }, [open])

  return (
    <div className={styles.wrap} ref={ref}>
      <button
        type="button"
        className={styles.trigger}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={`Sort tasks. Current: ${currentLabel(value)}`}
        onClick={() => setOpen((v) => !v)}
      >
        <SortIcon />
        <span className={styles.triggerText}>Sort</span>
        <span className={styles.triggerValue}>{currentLabel(value)}</span>
      </button>
      {open ? (
        <ul className={styles.menu} role="listbox" aria-label="Sort by">
          {OPTIONS.map((opt) => (
            <li key={opt.value} role="none">
              <button
                type="button"
                role="option"
                aria-selected={opt.value === value}
                className={`${styles.menuItem} ${opt.value === value ? styles.menuItemSelected : ''}`}
                onClick={() => {
                  onChange(opt.value)
                  setOpen(false)
                }}
              >
                {opt.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export default TaskSortMenu
